import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Copy,
  WhatsappLogo,
  ChatCircleText,
  X,
  CheckCircle,
  Clock,
  ArrowClockwise,
  ArrowSquareOut,
  ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * SignatureDialog — owner-side flow to send a contract for digital signature.
 *
 * Loads latest signature request for the receivable. If none exists (or the
 * user clicks "New link"), calls POST /finance/receivables/{id}/signature-request
 * to mint a fresh public token. Shows the shareable URL as a QR + copy + WhatsApp
 * + SMS deep-links. Polls every 4s while the signature is still pending so the
 * owner sees "Firmado" appear as soon as the client submits.
 */
export default function SignatureDialog({ receivable, onClose, onSigned }) {
  const [sig, setSig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get(
        `/finance/receivables/${receivable.receivable_id}/signature`,
      );
      setSig(data.signature || null);
    } catch (err) {
      // 404 is fine — no signature yet
      setSig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, [receivable.receivable_id]);

  // Poll while pending so we auto-reflect the "signed" state
  useEffect(() => {
    if (!sig || sig.status !== "pending") return;
    const t = setInterval(loadStatus, 4000);
    return () => clearInterval(t);
  }, [sig?.status, sig?.token]);

  const createRequest = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(
        `/finance/receivables/${receivable.receivable_id}/signature-request`,
      );
      setSig(data);
      toast.success("Enlace de firma listo — envíaselo al cliente");
    } catch (err) {
      toast.error("No se pudo generar el enlace");
    } finally {
      setCreating(false);
    }
  };

  // If we loaded and there's no active signature yet, auto-create one.
  useEffect(() => {
    if (!loading && !sig) createRequest();
  }, [loading]);

  // React to "signed" transition
  useEffect(() => {
    if (sig?.status === "signed") {
      toast.success("✓ Contrato firmado por el cliente");
      onSigned?.();
    }
  }, [sig?.status]);

  const shareUrl = sig?.share_url || (sig?.token ? `${window.location.origin}/firmar/${sig.token}` : "");
  const smsBody = sig?.sms_body || `Hola ${(receivable.client_name || "").split(" ")[0] || ""}! Aquí tienes tu contrato: ${shareUrl}`;
  const whatsappUrl = sig?.whatsapp_url || `https://wa.me/?text=${encodeURIComponent(smsBody)}`;
  const phoneDigits = (receivable.client_phone || "").replace(/[^0-9]/g, "");
  const smsHref = phoneDigits ? `sms:${phoneDigits}?body=${encodeURIComponent(smsBody)}` : `sms:?body=${encodeURIComponent(smsBody)}`;

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success("Enlace copiado"),
      () => toast.error("No se pudo copiar"),
    );
  };

  return (
    <div
      data-testid="rcv-signature-dialog"
      className="force-dark-scope fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col max-h-[92vh]"
      >
        {/* Header */}
        <div className="p-4 border-b border-white/8 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
              FIRMA DIGITAL · {receivable.contract_number || receivable.receivable_id}
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5">
              Enviar contrato a {(receivable.client_name || "").split(" ")[0]}
            </div>
            <div className="text-[11px] font-mono text-white/50 mt-1">
              El cliente abrirá el enlace en su teléfono, revisará el contrato y firmará con el dedo. Verás la firma incrustada en el PDF cuando termine.
            </div>
          </div>
          <button
            data-testid="rcv-signature-close"
            onClick={onClose}
            className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/8"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading || creating ? (
            <div className="py-12 text-center">
              <ArrowClockwise size={22} weight="bold" className="mx-auto mb-2 text-white/40 animate-spin" />
              <div className="text-[11px] font-mono text-white/40">
                {creating ? "Generando enlace seguro..." : "Cargando..."}
              </div>
            </div>
          ) : sig?.status === "signed" ? (
            <SignedState sig={sig} shareUrl={shareUrl} />
          ) : (
            <PendingState
              sig={sig}
              shareUrl={shareUrl}
              whatsappUrl={whatsappUrl}
              smsHref={smsHref}
              onCopy={copyLink}
              onNewLink={createRequest}
              creating={creating}
            />
          )}
        </div>

        <div className="p-3 border-t border-white/8 flex items-center justify-between gap-2 bg-black/20">
          <div className="text-[10px] font-mono text-white/40">
            {sig?.status === "signed" ? (
              <span className="text-emerald-300 flex items-center gap-1">
                <ShieldCheck size={11} weight="fill" /> Firma sellada con timestamp e IP
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Clock size={11} weight="bold" />
                Expira {sig?.expires_at ? sig.expires_at.slice(0, 10) : "—"}
              </span>
            )}
          </div>
          <button
            data-testid="rcv-signature-done"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function PendingState({ sig, shareUrl, whatsappUrl, smsHref, onCopy, onNewLink, creating }) {
  if (!sig) return null;
  return (
    <div className="space-y-4">
      {/* QR + URL */}
      <div className="rounded-xl border border-white/8 bg-black/30 p-4 flex items-center gap-4">
        <div className="bg-white p-2 rounded-lg shrink-0" data-testid="rcv-signature-qr">
          {shareUrl ? (
            <QRCodeSVG value={shareUrl} size={112} bgColor="#FFFFFF" fgColor="#0F1017" level="M" />
          ) : (
            <div className="w-28 h-28 bg-slate-200" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">
            Enlace único (válido 30 días)
          </div>
          <div
            data-testid="rcv-signature-url"
            className="text-[11px] font-mono text-emerald-300 break-all leading-relaxed"
          >
            {shareUrl}
          </div>
          <div className="text-[10px] font-mono text-white/30 mt-2">
            El cliente escanea el QR o abre el enlace desde su celular.
          </div>
        </div>
      </div>

      {/* Share buttons */}
      <div className="grid grid-cols-3 gap-2">
        <a
          data-testid="rcv-signature-whatsapp"
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          className="on-solid flex flex-col items-center justify-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-3 text-[11px] font-mono font-bold uppercase tracking-widest"
        >
          <WhatsappLogo size={20} weight="fill" />
          WhatsApp
        </a>
        <a
          data-testid="rcv-signature-sms"
          href={smsHref}
          className="flex flex-col items-center justify-center gap-1 rounded-lg border border-white/10 bg-black/30 hover:bg-black/50 text-white/80 px-3 py-3 text-[11px] font-mono font-bold uppercase tracking-widest"
        >
          <ChatCircleText size={20} weight="fill" />
          SMS
        </a>
        <button
          data-testid="rcv-signature-copy"
          onClick={onCopy}
          className="flex flex-col items-center justify-center gap-1 rounded-lg border border-white/10 bg-black/30 hover:bg-black/50 text-white/80 px-3 py-3 text-[11px] font-mono font-bold uppercase tracking-widest"
        >
          <Copy size={20} weight="fill" />
          Copiar
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-2 text-[10px] font-mono text-white/40 pt-1">
        <span className="flex items-center gap-1">
          <Clock size={10} weight="bold" className="text-amber-400 animate-pulse" />
          Esperando la firma del cliente...
        </span>
        <button
          data-testid="rcv-signature-new-link"
          onClick={onNewLink}
          disabled={creating}
          className="uppercase tracking-widest text-white/40 hover:text-white/80 disabled:opacity-40"
        >
          Generar nuevo enlace
        </button>
      </div>

      {/* Preview link */}
      <a
        data-testid="rcv-signature-preview"
        href={shareUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white/80 pt-1"
      >
        <ArrowSquareOut size={11} weight="bold" />
        Previsualizar como cliente
      </a>
    </div>
  );
}

function SignedState({ sig, shareUrl }) {
  const when = (sig.signed_at || "").slice(0, 19).replace("T", " ");
  // Convert /firmar/<token> → /api/public/contract/<token>/pdf on the same origin
  const pdfUrl = shareUrl.replace("/firmar/", "/api/public/contract/") + "/pdf";
  return (
    <div className="space-y-4" data-testid="rcv-signature-signed">
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-center">
        <CheckCircle size={40} weight="fill" className="mx-auto mb-2 text-emerald-400" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-1">
          Contrato firmado
        </div>
        <div className="text-sm font-heading font-bold text-white">
          {sig.signer_typed_name || sig.client_name_snapshot || "Cliente"}
        </div>
        <div className="text-[10px] font-mono text-white/60 mt-1">
          {when ? `${when} UTC` : ""}{sig.signed_ip ? ` · IP ${sig.signed_ip}` : ""}
        </div>
      </div>
      <a
        data-testid="rcv-signature-signed-pdf"
        href={pdfUrl}
        target="_blank"
        rel="noreferrer"
        className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-black/30 hover:bg-black/50 text-white/80 px-3 py-2 text-[11px] font-mono font-bold uppercase tracking-widest"
      >
        <ArrowSquareOut size={12} weight="bold" />
        Ver PDF firmado
      </a>
    </div>
  );
}
